/**
 * Voice mode — THE resolution rule, in one place.
 *
 * `widget_voice_mode` arrived after `widget_voice_assist`, so the fleet is
 * full of tenants with the old boolean and no mode. The mapping for them is a
 * business rule: an absent mode on a voice-on tenant means "classic" — the
 * original AI Voice feature they already had — never tap-to-talk, and never
 * silently off.
 *
 * Before this module that rule was written out SIX times (dashboard-api
 * settings read, widget-api preload build, three iframe config posts, the
 * snippet's config fallback). Six copies of one rule across three services is
 * exactly the drift CLAUDE.md's one-typed-place rule exists to prevent: the
 * first copy someone edits alone flips tenants into the wrong voice product.
 *
 * Pure module, zero imports, browser-safe — the widget snippet bundles it.
 */

export const VOICE_MODES = ["off", "classic", "tap_to_talk"] as const;
export type VoiceMode = (typeof VOICE_MODES)[number];

export function isVoiceMode(value: unknown): value is VoiceMode {
  return (
    typeof value === "string" && (VOICE_MODES as readonly string[]).includes(value)
  );
}

/**
 * Resolve the effective mode from a stored/transported value plus the legacy
 * boolean. An explicit valid mode always wins — including explicit "off" on a
 * voiceAssist=true tenant. Anything else (absent, null, junk) falls back to
 * the legacy mapping.
 */
export function resolveVoiceMode(mode: unknown, voiceAssist: boolean): VoiceMode {
  if (isVoiceMode(mode)) return mode;
  return voiceAssist ? "classic" : "off";
}
